import type { SavedBudgetRange } from '@modules/build-hub-creator/api/types';
import { BUDGET_NOT_SPECIFIED } from './format-build-hub-attributes';

/**
 * Type guard on budget data.
 */
export const isBudgetRange = (data: unknown): data is SavedBudgetRange => {
    return (
        data !== null &&
        typeof data === 'object' &&
        'min' in data &&
        'max' in data &&
        typeof data.min === 'number' &&
        typeof data.max === 'number'
    );
};

/**
 * Format a single budget value according to the new formatting rules:
 * - If >= $1m: display as $5m or $5.5m (no decimal if round number)
 * - If >= $1k and < $1m: display as $500k (rounded to nearest thousand)
 *   - If rounding to nearest thousand results in >= $1m, format as $1m
 * - If < $1k: display exact amount
 */
const formatSingleBudgetValue = (value: number): string => {
    if (value >= 1000000) {
        // Format as millions: $5m or $5.5m (no decimal if round number)
        const millions = value / 1000000;
        const rounded = Math.round(millions * 10) / 10;
        return `$${rounded % 1 === 0 ? rounded.toFixed(0) : rounded}m`;
    }

    if (value >= 1000) {
        // Format as thousands: $500k (rounded to nearest thousand)
        const roundedThousands = Math.round(value / 1000);
        // If rounding to nearest thousand results in >= 1000k (i.e., >= $1m), format as $1m
        if (roundedThousands >= 1000) {
            return '$1m';
        }
        return `$${roundedThousands}k`;
    }

    // Format exact amount for values < $1k
    return `$${value}`;
};

/**
 * Render an exact custom budget amount without compact rounding.
 */
const formatExactBudgetValue = (value: number): string => {
    const hasFractionalPart = !Number.isInteger(value);

    return `$${value.toLocaleString('en-NZ', {
        minimumFractionDigits: hasFractionalPart ? 2 : 0,
        maximumFractionDigits: 2,
    })}`;
};

/**
 * formatBudgetLabel is a function that formats the budget label.
 * It is used to display the budget label in the build hub.
 *
 * @param budget - The budget to format.
 * @returns The formatted budget label.
 * @example
 * formatBudgetLabel(100000) // returns '$100k'
 * formatBudgetLabel(1500000) // returns '$1.5m'
 * formatBudgetLabel({ min: 0, max: 5000000 }) // returns '< $5m'
 */
export const formatBudgetLabel = (
    budget: number | SavedBudgetRange,
): string => {
    // Handle single numeric budget value
    if (!isBudgetRange(budget)) {
        return formatSingleBudgetValue(budget);
    }

    const { min, max } = budget;

    // Handle budget not specified
    if (min === 0 && max === 0) {
        return BUDGET_NOT_SPECIFIED;
    }

    // If min is 0, display as "< $X" format
    if (min === 0 && max > 0) {
        return `< ${formatSingleBudgetValue(max)}`;
    }

    // If min equals max, format as single value
    if (min === max) {
        return formatExactBudgetValue(min);
    }

    // Handle unbounded ranges (max is 0 but min > 0)
    if (min > 0 && max === 0) {
        return `${formatSingleBudgetValue(min)}+`;
    }

    // Format as range: $X - $Y
    return `${formatSingleBudgetValue(min)} - ${formatSingleBudgetValue(max)}`;
};

/**
 * Checks if a budget value has been specified.
 * Returns false if budget is a range with both min and max equal to 0.
 */
export const hasBudgetValue = (budget: number | SavedBudgetRange): boolean => {
    if (isBudgetRange(budget)) {
        return !(budget.min === 0 && budget.max === 0);
    }
    return budget > 0;
};
